/**
 * `useFreshAccessToken` — hands out the cookie-stored Cognito access token,
 * proactively refreshing it before it expires so consumers (the WebSocket
 * hooks) don't try to handshake with a stale token.
 *
 * Why this exists: when the access token expires, the WS handshake to
 * `wss://realtime-{stage}.3mote.com/?token=...` gets HTTP 401 from the
 * `$connect` Lambda. API Gateway closes the connection with code 1006
 * (abnormal). 1006 is NOT in the 4401/4403 close-code set the WS hook
 * recognizes as auth failure (those codes only fire on already-open
 * connections that get closed for auth), so the hook reconnects forever
 * with the same stale token. Pre-empting expiry at the consumer layer is
 * the cleanest fix; the WS hook itself stays protocol-agnostic.
 *
 * Both consumers (`useGlobalRealtime` and `useRealtimeBoard`) use this
 * hook. Each runs its own refresh interval but they share Cognito's
 * cookie-stored refresh token, so duplicate refresh calls land on the
 * same access-token response — no harm.
 */

import { useCallback, useEffect, useState } from 'react'

import { jwtSecondsUntilExpiry, refreshSession } from './cognito'
import { getAccessToken } from './cookie-helpers'

export interface FreshAccessTokenResult {
  /** Latest access token (auto-refreshed). `null` when signed out. */
  readonly token: string | null
  /** Manually trigger a refresh (e.g. from a WS `onAuthFailure` handler). */
  readonly refresh: () => Promise<void>
}

/**
 * Refresh threshold: if the token has fewer than this many seconds left,
 * call `refreshSession()` before handing it out. 60s gives the WS
 * handshake a comfortable buffer.
 */
const REFRESH_THRESHOLD_SECONDS = 60

/**
 * Re-check token freshness every N ms. Cognito access tokens are 1h, so
 * 5min cadence gives at least one tick of warning before expiry.
 */
const REFRESH_CHECK_INTERVAL_MS = 5 * 60 * 1000

export function useFreshAccessToken(): FreshAccessTokenResult {
  const [token, setToken] = useState<string | null>(() => getAccessToken())

  const refresh = useCallback(async () => {
    try {
      const next = await refreshSession()
      setToken(next.accessToken)
    } catch {
      // Refresh failed — let the apiClient's 401 bounce path own the
      // /login redirect; hold the stale value here so the WS hook can
      // observe the failure via its own close-handler if it's mid-flight.
      setToken(getAccessToken())
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const refreshIfStale = async () => {
      const current = getAccessToken()
      if (!current) {
        if (!cancelled) setToken(null)
        return
      }
      const ttl = jwtSecondsUntilExpiry(current)
      if (ttl !== null && ttl < REFRESH_THRESHOLD_SECONDS) {
        try {
          const next = await refreshSession()
          if (!cancelled) setToken(next.accessToken)
        } catch {
          if (!cancelled) setToken(current)
        }
        return
      }
      if (!cancelled) setToken((prev) => (prev === current ? prev : current))
    }
    void refreshIfStale()
    const interval = setInterval(() => void refreshIfStale(), REFRESH_CHECK_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  return { token, refresh }
}
